import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MaskHappy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BTN = "border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white transition-colors duration-100 disabled:opacity-40";

export function useLoginAs() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const can = Boolean(user) && !user.impersonated_by
    && (user.role === "super_admin" || (user.permissions || []).includes("impersonate_users"));
  const loginAs = async (u) => {
    try {
      await api.post(`/auth/impersonate/${u.id}`);
      toast.success(`Now viewing as ${u.email}`);
      await refresh?.();
      nav("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't log in as this buyer");
    }
  };
  return { can, loginAs };
}

export function LoginAsUserButton({ u, testId }) {
  const { can, loginAs } = useLoginAs();
  const [busy, setBusy] = useState(false);
  if (!can) return null;
  const go = async () => {
    setBusy(true);
    await loginAs(u);
    setBusy(false);
  };
  return (
    <button type="button" onClick={go} disabled={busy} className={BTN} data-testid={testId} title={`See the dashboard as ${u.email}`}>
      <MaskHappy size={10} weight="bold" /> {busy ? "Opening…" : "Log in as"}
    </button>
  );
}

// Log in as a buyer's account (asks which one when there are several). `url` lists the logins.
export function LoginAsBuyerButton({ url, testId, className = "" }) {
  const { can, loginAs } = useLoginAs();
  const [logins, setLogins] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!can) return null;
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(url);
      if (!data.logins.length) toast.error("This buyer has no login yet. Invite them first.");
      else if (data.logins.length === 1) await loginAs(data.logins[0]);
      else setLogins(data.logins);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load this buyer's logins");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className={`relative inline-flex align-middle ${className}`}>
      <button type="button" onClick={open} disabled={busy} className={BTN} data-testid={testId}>
        <MaskHappy size={11} weight="bold" /> {busy ? "Opening…" : "Log in as buyer"}
      </button>
      {logins && (
        <div className="absolute left-0 top-full z-30 mt-1 w-64 border-2 border-black bg-white shadow-[4px_4px_0_0_#000]" data-testid={`${testId}-menu`}>
          <div className="label-mono border-b border-zinc-300 px-2 py-1.5">Pick a login</div>
          {logins.map((u) => (
            <button key={u.id} type="button" onClick={() => loginAs(u)} data-testid={`${testId}-user-${u.id}`}
                    className="block w-full truncate border-b border-zinc-200 px-2 py-1.5 text-left text-[11px] hover:bg-zinc-100">
              {u.email}
            </button>
          ))}
          <button type="button" onClick={() => setLogins(null)} className="block w-full px-2 py-1.5 text-left text-[10px] uppercase tracking-widest text-zinc-600 hover:bg-zinc-100"
                  data-testid={`${testId}-cancel`}>Cancel</button>
        </div>
      )}
    </span>
  );
}
